import { FormModal } from '@/components/form-modal';
import { Button } from '@/components/ui/button';
import { DialogDescription } from '@/components/ui/dialog';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/hooks/use-toast';
import {
  bulkSoftDeleteStudentGroups,
  getStudentGroup,
  getStudentGroups,
  softDeleteStudentGroup,
} from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import type { StudentGroupWithScheduleAndLevel } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { ReactNode } from 'react';
import { z } from 'zod';

const formSchema = z.object({});

export interface DeleteStudentGroupProps {
  studentGroups?: StudentGroupWithScheduleAndLevel[];
  studentGroupId?: string;
  trigger?: ReactNode;
  afterDelete?: () => void;
  dataTestId?: string;
  triggerTestId?: string;
}

export function DeleteStudentGroup({
  studentGroups = [],
  studentGroupId,
  trigger,
  afterDelete,
  dataTestId,
  triggerTestId,
}: DeleteStudentGroupProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync: softDeleteMutate } = useMutation({
    mutationFn: ({ studentGroupIds }: { studentGroupIds: string[] }) =>
      studentGroups.length > 0
        ? bulkSoftDeleteStudentGroups({ studentGroupIds })
        : softDeleteStudentGroup(studentGroupIds[0]),
  });

  const onSubmit = ({ closeModal }: { closeModal: () => void }) => {
    const studentGroupIds =
      studentGroups.length > 0 ? studentGroups.map(({ id }) => id) : studentGroupId ? [studentGroupId] : [];
    if (studentGroupIds.length === 0) {
      return;
    }

    return softDeleteMutate({ studentGroupIds })
      .then(() => {
        toast({ title: `${studentGroupIds.length} class${studentGroupIds.length === 1 ? '' : 'es'} deleted` });
        closeModal();
        afterDelete?.();
        void queryClient.invalidateQueries(getStudentGroups());
        for (const id of studentGroupIds) {
          void queryClient.invalidateQueries(getStudentGroup(id));
        }
      })
      .catch((error: unknown) => {
        toast({
          title: 'Failed to delete classes',
          description: airServerErrorFrom(error)?.userFacingMessage,
          variant: 'destructive',
        });
      });
  };

  const count = studentGroups.length > 0 ? studentGroups.length : Number(Boolean(studentGroupId));
  const hasFutureSessions = studentGroups.some((studentGroup) => (studentGroup.futureSessions ?? 0) > 0);
  const hasNonArchived = studentGroups.some((studentGroup) => studentGroup.status !== 'archived');
  const isDeleteDisabled = count === 0 || hasFutureSessions || hasNonArchived;
  const isBulkAction = studentGroups.length > 0;

  return (
    <FormModal
      modalTitle={isBulkAction ? 'Delete classes' : 'Delete class'}
      dataTestId={dataTestId}
      trigger={
        trigger ??
        (isBulkAction ? (
          <DropdownMenuItem
            onSelect={(event) => event.preventDefault()}
            disabled={isDeleteDisabled}
            data-testid={triggerTestId}
          >
            Delete classes
          </DropdownMenuItem>
        ) : (
          <Button size="sm" variant="secondary" aria-label="Delete class">
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
        ))
      }
      onSubmit={onSubmit}
      dialogContentProps={{ className: 'max-w-md' }}
      submitButtonProps={{ variant: 'destructive', children: 'Delete', disabled: isDeleteDisabled }}
      formSchema={formSchema}
      confirmBtnLabel="Delete"
    >
      {() => (
        <DialogDescription>
          Are you sure you want to delete{' '}
          {isBulkAction ? `${count} selected class${count === 1 ? '' : 'es'}` : 'this class'}?{' '}
          {count === 1 ? 'It' : 'They'} will no longer appear in Active or Archived lists. This action cannot be undone.
        </DialogDescription>
      )}
    </FormModal>
  );
}
